import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, getApiBaseUrl } from '../services/api';

interface UserData {
  username: string;
  token: string;
  name?: string;
  role?: string;
}

interface AuthContextType {
  user: UserData | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isServerAvailable: boolean;
  isTokenExpired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isServerAvailable, setIsServerAvailable] = useState(true);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const name = localStorage.getItem('name');
    const role = localStorage.getItem('role');
    
    if (token && username) {
      // Verificar si el token es válido haciendo una petición de prueba
      const validateToken = async () => {
        try {
          // Usar el endpoint que requiere autenticación para validar el token
          const response = await fetch(`${getApiBaseUrl()}/api/health/auth`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            mode: 'cors'
          });
          
          if (response.ok) {
            setUser({ username, token, name: name || undefined, role: role || undefined });
          } else if (response.status === 401) {
            // Token expirado
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            localStorage.removeItem('name');
            localStorage.removeItem('role');
            setUser(null);
            setIsTokenExpired(true);
          }
        } catch (error) {
          console.error('Error validando token:', error);
          setIsServerAvailable(false);
          // Si hay error de red, verificar si el servidor está disponible
          try {
            const healthResponse = await fetch(`${getApiBaseUrl()}/api/health`, {
              method: 'GET',
              mode: 'cors'
            });
            if (healthResponse.ok) {
              // Servidor disponible pero token inválido
              localStorage.removeItem('token');
              localStorage.removeItem('username');
              localStorage.removeItem('name');
              localStorage.removeItem('role');
              setUser(null);
            } else {
              // Servidor no disponible
              setIsServerAvailable(false);
            }
          } catch {
            // Error de conexión
            setIsServerAvailable(false);
          }
        }
      };
      
      validateToken();
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await authAPI.login(username, password);
      const userData: UserData = { 
        username, 
        token: response.token,
        name: response.user?.name,
        role: response.user?.role
      };
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('username', username);
      if (response.user?.name) {
        localStorage.setItem('name', response.user.name);
      }
      if (response.user?.role) {
        localStorage.setItem('role', response.user.role);
      }
      setUser(userData);
      setIsTokenExpired(false);
      setIsServerAvailable(true);
      
      return true;
    } catch (error) {
      console.error('Error en login:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Llamar al endpoint de logout en el backend
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          mode: 'cors'
        }).catch(() => {
          // Ignorar errores de conexión al cerrar sesión
        });
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      // Limpiar siempre el estado local
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('name');
      localStorage.removeItem('role');
      setUser(null);
      setIsTokenExpired(false);
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    isServerAvailable,
    isTokenExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
